import { ToastrService } from 'ngx-toastr';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { take } from 'rxjs/operators';
import { UntypedFormGroup, AbstractControl, UntypedFormControl, Validators, ValidationErrors } from '@angular/forms';
import { Subscription } from 'rxjs';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';

import { LicenseService } from '../../core/services/http-services/license.service';
import { ModalRequestLicenseComponent } from '../modal-request-license/modal-request-license.component';
import { LicenseDialogMessagesEnum } from '../../core/enums/toastr-messages/license-dialog-messages.enum';
import { AppConstants } from '../../app.constants';
import { DeleteDialogService } from '../../core/services/dialog-services/delete-dialog.service';

@Component({
  selector: 'app-modal-install-license',
  templateUrl: './modal-install-license.component.html',
  styleUrls: ['./modal-install-license.component.scss']
})
export class ModalInstallLicenseComponent implements OnInit, OnDestroy {

  public form: UntypedFormGroup;
  public invalidKeys: string[] = [];
  public uploadedFile: File = null;
  public isPendingLicenseActivation: boolean = false;

  private subscriptions: Subscription = new Subscription();

  constructor(
    private readonly licenseService: LicenseService,
    private readonly toastrService: ToastrService,
    private readonly dialog: MatDialog,
    private readonly dialogRef: MatDialogRef<ModalInstallLicenseComponent>,
    private readonly deleteDialogService: DeleteDialogService
  ) { }

  get licenseKey(): AbstractControl {
    return this.form.get('licenseKey');
  }

  ngOnInit(): void {
    this.form = new UntypedFormGroup({ licenseKey: new UntypedFormControl(null, [Validators.required, this.keyValidator.bind(this)]) });
  }

  onBlur(event: FocusEvent): void {
    this.licenseKey.patchValue((event.target as HTMLInputElement).value.trim());
  }

  onSubmit(): void {
    if (this.licenseKey.invalid || !this.licenseKey.value) {
      return;
    }

    this.isPendingLicenseActivation = true;

    this.subscriptions.add(this.licenseService.activateLicense(this.licenseKey.value)
      .pipe(
        take(1)
      ).subscribe((isLicense) => {
        if (isLicense) {
          this.toastrService.success(LicenseDialogMessagesEnum.LicenseActivatedSuccess);
          this.dialogRef.close();
        } else {
          this.toastrService.error(LicenseDialogMessagesEnum.InvalidLicenseKey);
          this.licenseKey.setErrors({ invalidKey: true });
          this.invalidKeys.push(this.licenseKey.value);
        }
        this.isPendingLicenseActivation = false;
      }, () => this.isPendingLicenseActivation = false));
  }

  onClose(): void {
    if (this.isPendingLicenseActivation) {
      this.subscriptions.add(
        this.deleteDialogService.show(LicenseDialogMessagesEnum.CloseModalWindow).subscribe((isToClose: boolean) => {
          if (isToClose) {
            this.licenseService.checkIsToShowNotification(this.licenseService.licenseStatusModel);
            this.dialogRef.close();
          }
        })
      );
    } else {
      this.licenseService.checkIsToShowNotification(this.licenseService.licenseStatusModel);
      this.dialogRef.close();
    }
  }

  openGenerateLicenseDialog(): void {
    this.dialog.open(ModalRequestLicenseComponent, {
      width: AppConstants.MODAL_WINDOW_WIDTH
    });
    this.form.reset();
  }

  newFileUploaded(): void {
    this.subscriptions.add(
      this.licenseService.activateLicenseFromFile(this.uploadedFile).subscribe((isLicense: boolean) => {
        if (isLicense) {
          this.toastrService.success(LicenseDialogMessagesEnum.FileUploadedSuccess);
          this.dialogRef.close();
        } else {
          this.toastrService.error(LicenseDialogMessagesEnum.FileUploadedFailed, LicenseDialogMessagesEnum.LicenseActivatedFailed);
        }
      })
    );
  }

  newFileUpload(fileToUpload: File): void {
    if (fileToUpload.name.endsWith(AppConstants.LIC_FILE_TYPE) || fileToUpload.name.endsWith(AppConstants.OLIC_FILE_TYPE)) {
      this.uploadedFile = fileToUpload;
    } else {
      this.toastrService.error(LicenseDialogMessagesEnum.WrongFileTypeSelectedError, LicenseDialogMessagesEnum.WrongFileTypeSelectedTitle);
    }

  }

  private keyValidator(control: AbstractControl): ValidationErrors {
    if (!control || !control.value || !this.invalidKeys.length) {
      return null;
    }

    const nameExists = this.invalidKeys.some(key => control.value.trim().toLowerCase() === key.toLowerCase());

    if (nameExists) {
      return { invalidKey: true };
    }

    return null;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
